import React, { useState, useEffect } from 'react';
import { Card, Col, Container, Row, Table, Modal, Button, Form, Dropdown } from 'react-bootstrap';
import BreadCrumb from 'Common/BreadCrumb';
import Swal from 'sweetalert2'; // Import SweetAlert2
import axios from 'axios'; // Import axios

interface FeedbackData {
  id: number;
  subject: string;
  message: string;
  rating: number;
  status: string;
  created_at: string;
  updated_at: string;
}

const Feedback = () => {
  document.title = 'Feedback | CITY SERVICE';

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    rating: 1,
    status: 'pending'
  }); // Updated form data state
  const [editMode, setEditMode] = useState(false); // State for edit mode
  const [editId, setEditId] = useState<number | null>(null); // State for edit ID
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [tableData, setTableData] = useState<FeedbackData[]>([]); // Ensure default value is an empty array

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditId(null);
    setFormData({ subject: '', message: '', rating: 1, status: 'pending' });
  };

  const handleEditModal = (feedback: FeedbackData) => {
    setEditMode(true);
    setEditId(feedback.id);
    setFormData({ 
      subject: feedback.subject, 
      message: feedback.message, 
      rating: feedback.rating, 
      status: feedback.status 
    });
    setShowModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: id === 'rating' ? parseInt(value) : value });
  };

  const handleFormSubmit = async () => {
    setIsLoading(true); // Start loading
    Swal.fire({
      title: editMode ? 'Updating...' : 'Saving...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      let response;
      if (editMode && editId) {
        // Update existing feedback
        response = await axios.patch(
          `/feedback/${editId}`,
          {
            subject: formData.subject,
            message: formData.message,
            rating: formData.rating,
            status: formData.status,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`, // Add token to Authorization header
            },
          }
        );
        Swal.fire('Success', `Feedback "${response.data.subject}" updated successfully!`, 'success');
      } else {
        // Create new feedback
        response = await axios.post(
          '/feedback',
          {
            subject: formData.subject,
            message: formData.message,
            rating: formData.rating,
            status: formData.status,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`, // Add token to Authorization header
            },
          }
        );
        Swal.fire('Success', `Feedback "${response.data.subject}" added successfully!`, 'success');
      }

      setShowModal(false);
      setEditMode(false);
      setEditId(null);
      setFormData({ subject: '', message: '', rating: 1, status: 'pending' }); // Reset form
      fetchFeedback(); // Refetch feedback to update the table
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'An unknown error occurred',
        'error'
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const fetchFeedback = async () => {
    setIsLoading(true); // Start loading
    Swal.fire({
      title: 'Loading...',
      html: 'Please wait while we fetch the data.',
      timerProgressBar: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get('/feedback', {
        headers: {
          Authorization: `Bearer ${token}`, // Add token to Authorization header
        },
      });

      setTableData(response.data.data || []); // Update table data with API response
      Swal.fire({
        icon: 'success',
        title: 'Data Loaded',
        showConfirmButton: false,
        timer: 1000, // Smooth transition after loading
      });
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to fetch feedback',
        'error'
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true); // Start loading
        try {
          const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
          if (!token) {
            throw new Error('Authentication token is missing. Please log in again.');
          }

          await axios.delete(`/feedback/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`, // Add token to Authorization header
            },
          });

          Swal.fire('Deleted!', 'The feedback has been deleted.', 'success');
          setTableData((prevData) => prevData.filter((item) => item.id !== id)); // Remove deleted item from table
        } catch (error) {
          Swal.fire(
            'Error',
            (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to delete feedback',
            'error'
          );
        } finally {
          setIsLoading(false); // End loading
        }
      }
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="badge bg-warning">Pending</span>;
      case 'reviewed':
        return <span className="badge bg-info">Reviewed</span>;
      case 'resolved':
        return <span className="badge bg-success">Resolved</span>;
      case 'rejected':
        return <span className="badge bg-danger">Rejected</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  const getRatingStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i 
          key={i} 
          className={`ri-star${i <= rating ? '-fill' : '-line'}`} 
          style={{ color: i <= rating ? '#ffc107' : '#dee2e6' }}
        ></i>
      );
    }
    return stars;
  };

  useEffect(() => {
    fetchFeedback(); // Fetch feedback on component mount
  }, []); // Fetch feedback on component mount

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Feedback" title="Feedback" />

          <Row>
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0"></h5>
                    <div className="ms-auto"> {/* Add ms-auto to push the button to the right */}
                      <button
                        className="btn btn-dark d-flex align-items-center gap-1"
                        onClick={handleShowModal}
                      >
                        <i className="ri-add-line"></i>
                        <span>Create</span>
                      </button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Subject</th>
                        <th>Message</th>
                        <th>Rating</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(tableData) && tableData.length > 0 ? ( // Ensure tableData is an array and not empty
                        tableData.map((feedback, index) => (
                          <tr key={feedback.id}>
                            <td>{index + 1}</td>
                            <td>{feedback.subject}</td>
                            <td>
                              <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {feedback.message}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-1">
                                {getRatingStars(feedback.rating)}
                                <span className="ms-1">({feedback.rating})</span>
                              </div>
                            </td>
                            <td>{getStatusBadge(feedback.status)}</td>
                            <td>{new Date(feedback.created_at).toLocaleDateString()}</td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle variant="dark" id={`action-dropdown-${feedback.id}`} className="btn-sm">
                                  <i className="ri-more-2-fill"></i> {/* Add an icon for the dropdown toggle */}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => handleEditModal(feedback)}>
                                    <i className="ri-edit-line me-2"></i> Edit {/* Add an edit icon */}
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleDelete(feedback.id)}>
                                    <i className="ri-delete-bin-line me-2"></i> Delete {/* Add a delete icon */}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center">
                            No feedback available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for Adding/Editing Feedback */}
        <Modal show={showModal} onHide={handleCloseModal} backdrop="static" centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? 'Edit Feedback' : 'Add Feedback'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="subject">Subject</Form.Label>
                <Form.Control
                  type="text"
                  id="subject"
                  placeholder="Enter feedback subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  isInvalid={!formData.subject.trim()} // Validation feedback
                />
                <Form.Control.Feedback type="invalid">
                  Subject is required.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="message">Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  id="message"
                  placeholder="Enter feedback message"
                  value={formData.message}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  isInvalid={!formData.message.trim()} // Validation feedback
                />
                <Form.Control.Feedback type="invalid">
                  Message is required.
                </Form.Control.Feedback>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="rating">Rating</Form.Label>
                    <Form.Control
                      as="select"
                      id="rating"
                      value={formData.rating}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    >
                      <option value={1}>1 Star - Very Poor</option>
                      <option value={2}>2 Stars - Poor</option>
                      <option value={3}>3 Stars - Average</option>
                      <option value={4}>4 Stars - Good</option>
                      <option value={5}>5 Stars - Excellent</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="status">Status</Form.Label>
                    <Form.Control
                      as="select"
                      id="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    >
                      <option value="pending">Pending</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="resolved">Resolved</option>
                      <option value="rejected">Rejected</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="dark" onClick={handleCloseModal} disabled={isLoading}>
              Close
            </Button>
            <Button variant="dark" onClick={handleFormSubmit} disabled={isLoading}>
              {isLoading ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? 'Update' : 'Save')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default Feedback;
